import {
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import FormWrapper from "../FormWrapper/FormWrapper";
import InputField from "../../Shared_components/InputField";
import DropDown from "../../Shared_components/DropDown";
import TextArea from "../../Shared_components/TextArea";
import { TextField, MenuItem, Typography, Autocomplete } from "@mui/material";
import {
  validatePostCode,
  validateAndFormatPostCode,
  validateAndFormatPostCodeFromWeb,
} from "../../../../utils/postCodeValidation";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../../utils/contactValidation";

const ReceiverForm = forwardRef(
  (
    {
      receiverName,
      receiverEmail,
      receiverMobileNumber,
      receiverCountry,
      receiverLandmark,
      receiverCity,
      receiverAddress,
      receiverDoorNo,
      receiverStreetName,
      receiverRemarks,
      updateFields,
      receiverPostCode,
      fetchedFormData,
      embedded = false,
      emailRequired = true,
    },
    ref,
  ) => {
    const { country, city } = fetchedFormData;
    const [receiverPostCodeError, setReceiverPostCodeError] = useState("");
    const [receiverEmailError, setReceiverEmailError] = useState("");
    const [receiverMobileError, setReceiverMobileError] = useState("");

    const selectedCountry = country?.find(
      (c) => String(c.id) === String(receiverCountry),
    );
    const countryName = selectedCountry?.name?.toLowerCase() || "";

    const isUK =
      receiverCountry === "22" ||
      receiverCountry === 22 ||
      countryName === "uk";

    const isBangladesh =
      countryName.includes("bangladesh") || countryName === "bd";

    const requiresPostCodeValidation = isUK || isBangladesh;

    const validateReceiverPostCode = useCallback(async () => {
      const value = (receiverPostCode || "").trim();

      if (!requiresPostCodeValidation) {
        setReceiverPostCodeError("");
        return { valid: true };
      }

      if (!value) {
        setReceiverPostCodeError("Post code is required");
        return { valid: false, error: "Post code is required" };
      }

      try {
        const validation = await validateAndFormatPostCodeFromWeb(
          value,
          receiverCountry,
          country || [],
        );
        if (!validation.isValid) {
          setReceiverPostCodeError(validation.error);
          return { valid: false, error: validation.error };
        }
        setReceiverPostCodeError("");
        if (validation.formatted !== value) {
          updateFields({ receiverPostCode: validation.formatted });
        }
        return { valid: true };
      } catch (error) {
        console.error("Post code validation error:", error);
        const formatValidation = validateAndFormatPostCode(
          value,
          receiverCountry,
          country || [],
        );
        if (!formatValidation.isValid) {
          setReceiverPostCodeError(formatValidation.error);
          return { valid: false, error: formatValidation.error };
        }
        setReceiverPostCodeError("");
        if (
          formatValidation.formatted &&
          formatValidation.formatted !== value
        ) {
          updateFields({ receiverPostCode: formatValidation.formatted });
        }
        return { valid: true };
      }
    }, [
      receiverPostCode,
      receiverCountry,
      country,
      requiresPostCodeValidation,
      updateFields,
    ]);

    const validateReceiverContact = useCallback(() => {
      const emailResult = validateEmail(receiverEmail, { required: emailRequired });
      const phoneResult = validatePhoneNumber(receiverMobileNumber);

      setReceiverEmailError(emailResult.valid ? "" : emailResult.error);
      setReceiverMobileError(phoneResult.valid ? "" : phoneResult.error);

      return {
        valid: emailResult.valid && phoneResult.valid,
        emailError: emailResult.error,
        phoneError: phoneResult.error,
      };
    }, [
      receiverEmail,
      receiverMobileNumber,
      emailRequired,
    ]);

    useImperativeHandle(ref, () => ({
      validatePostCode: validateReceiverPostCode,
      validateContact: validateReceiverContact,
    }));

    const filteredCities = useMemo(() => {
      if (!receiverCountry || !city) return [];
      return city.filter(
        (item) => String(item.countryId) === String(receiverCountry),
      );
    }, [receiverCountry, city]);

    const textFieldStyle = {
      backgroundColor: "white",
      borderRadius: "8px",
      width: "100%",
      /* Hide MUI floating label (user wants normal inputs without label in/above the box) */
      "& .MuiInputLabel-root": { display: "none" },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#ccc",
          borderRadius: "12px",
        },
        "&:hover fieldset": {
          borderColor: "#1976d2",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#1976d2",
        },
      },
    };

    const formFields = (
      <div className={`flex flex-col gap-6 ${embedded ? "" : "p-4 sm:p-6"}`}>
        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Receiver Name</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required
              id="receiver-name"
              placeholder="Full name"
              variant="outlined"
              value={receiverName}
              onChange={(e) => updateFields({ receiverName: e.target.value })}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">
              {emailRequired ? "Email" : "Email (optional)"}
            </div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required={emailRequired}
              id="receiver-email"
              placeholder={
                emailRequired ? "Email address" : "Email address (optional)"
              }
              variant="outlined"
              type="email"
              value={receiverEmail}
              onChange={(e) => {
                updateFields({ receiverEmail: e.target.value });
                if (receiverEmailError) setReceiverEmailError("");
              }}
              onBlur={() => {
                const result = validateEmail(receiverEmail, {
                  required: emailRequired,
                });
                setReceiverEmailError(result.valid ? "" : result.error);
              }}
              error={!!receiverEmailError}
              helperText={receiverEmailError}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Mobile Number</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required
              id="receiver-mobile"
              placeholder="Mobile number"
              variant="outlined"
              value={receiverMobileNumber}
              onChange={(e) => {
                const newValue = e.target.value;
                // Allow only empty string or numeric characters
                if (/^\d*$/.test(newValue)) {
                  updateFields({ receiverMobileNumber: newValue });
                  if (receiverMobileError) setReceiverMobileError("");
                }
              }}
              onBlur={() => {
                const result = validatePhoneNumber(receiverMobileNumber);
                setReceiverMobileError(result.valid ? "" : result.error);
              }}
              error={!!receiverMobileError}
              helperText={receiverMobileError}
            />
          </div>

          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Country</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              select
              required
              id="country"
              variant="outlined"
              value={receiverCountry}
              onChange={(e) => {
                // Reset city when country changes
                updateFields({
                  receiverCountry: e.target.value,
                  receiverCity: "",
                });
              }}
            >
              {/* Add this placeholder item */}
              <MenuItem
                value=""
                disabled
                sx={{ color: "text.disabled", fontStyle: "italic" }}
              >
                Select Country
              </MenuItem>

              {country?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>

        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">City</div>
            <Autocomplete
              id="city"
              options={filteredCities} // Use filtered list
              getOptionLabel={(option) => option.name || ""}
              value={
                filteredCities.find(
                  (item) => String(item.id) === String(receiverCity),
                ) || null
              }
              onChange={(e, newValue) =>
                updateFields({ receiverCity: newValue ? newValue.id : "" })
              }
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              disabled={!receiverCountry} // Disable if no country
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={textFieldStyle}
                  fullWidth
                  required
                  placeholder="Search city..."
                  variant="outlined"
                  disabled={!receiverCountry} // Disable visual input
                />
              )}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">
              Nearest Landmark (Optional)
            </div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id="landmark"
              placeholder="Landmark"
              variant="outlined"
              value={receiverLandmark}
              onChange={(e) =>
                updateFields({ receiverLandmark: e.target.value })
              }
            />
          </div>
        </div>

        {/* <div className="flex w-full gap-6">
          <DropDown
            required={true}
            alltimeValue={receiverCity}
            valueUpdate={(e) => updateFields({ receiverCity: e.target.value })}
            placeHolder={"City"}
          >
            {city?.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </DropDown>

          <InputField
            required={false}
            valueUpdate={(e) => updateFields({ receiverLandmark: e.target.value })}
            type={"text"}
            placeHolder={"Nearest Landmark (Optional)"}
            alltimeValue={receiverLandmark}
          />
        </div> */}

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">Post Code</div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            required
            id="receiver-post-code"
            placeholder="Post code"
            variant="outlined"
            value={receiverPostCode}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              updateFields({ receiverPostCode: value });
              // Clear error when user starts typing
              if (receiverPostCodeError) {
                setReceiverPostCodeError("");
              }
            }}
            onBlur={() => {
              if (requiresPostCodeValidation) {
                validateReceiverPostCode();
              } else {
                setReceiverPostCodeError("");
              }
            }}
            error={requiresPostCodeValidation && !!receiverPostCodeError}
          />
          {requiresPostCodeValidation && receiverPostCodeError && (
            <p className="text-red-500 text-sm mt-1 ml-2">
              {receiverPostCodeError}
            </p>
          )}
        </div>

        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Door No</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id="receiver-door-no"
              placeholder="Door / flat / unit number"
              variant="outlined"
              value={receiverDoorNo}
              onChange={(e) => updateFields({ receiverDoorNo: e.target.value })}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Street Name</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id="receiver-street-name"
              placeholder="Street name"
              variant="outlined"
              value={receiverStreetName}
              onChange={(e) =>
                updateFields({ receiverStreetName: e.target.value })
              }
            />
          </div>
        </div>

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">Remarks (Optional)</div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            multiline
            rows={2}
            id="receiver-remarks"
            placeholder="Any additional notes for delivery"
            variant="outlined"
            value={receiverRemarks}
            onChange={(e) => updateFields({ receiverRemarks: e.target.value })}
          />
        </div>

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">
            Additional Address Information
          </div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            multiline
            rows={4}
            required
            id="receiver-address"
            placeholder="Enter full address details..."
            variant="outlined"
            value={receiverAddress}
            onChange={(e) => updateFields({ receiverAddress: e.target.value })}
          />
        </div>
      </div>
    );

    if (embedded) {
      return formFields;
    }

    return (
      <FormWrapper title="Receiver Details">
        <Typography
          variant="h5"
          className="text-center"
          sx={{
            fontWeight: 700,
            marginBottom: "8px",
            color: "hsl(var(--foreground))",
            textAlign: "center",
          }}
        >
          Receiver Information Form
        </Typography>
        <Typography
          variant="body2"
          className="text-center"
          sx={{ color: "hsl(var(--muted-foreground))", marginBottom: "16px" }}
        >
          Please fill in your receiver information below.
        </Typography>
        {formFields}
      </FormWrapper>
    );
  },
);

ReceiverForm.displayName = "ReceiverForm";

export default ReceiverForm;
